import { createContext, useContext, useState, useMemo, useCallback } from 'react';
import { userInfoSync } from 'hub-http/userInfo';
import { orderedSteps } from '../../constants/ordered-form-steps';
import { useAllowedAssets } from '../../hooks/use-allowed-assets';
import { useCreateSyncTask } from '../../hooks/mutations/use-create-sync-task';
import { useCopyAssetTaskStatus } from '../../hooks/queries/use-copy-asset-task-status';
import { usePortableKeys } from '../../hooks/queries/use-portable-keys';
import { jsx as _jsx } from "react/jsx-runtime";
const AssetCopierContext = /*#__PURE__*/createContext(undefined);
export function AssetCopierProvider(props) {
  const {
    children,
    assets,
    portableType,
    onCopyComplete
  } = props;
  const [currentStep, setCurrentStep] = useState('CHOOSE_DESTINATION');
  const [selectedTargetHub, setSelectedTargetHub] = useState(null);
  const {
    portal
  } = userInfoSync();
  const [allowedAssets, disallowedAssets] = useAllowedAssets(assets);
  const onError = () => {
    setCurrentStep('ERROR');
  };
  const {
    allPortableKeys,
    portableKeyMap,
    fetchPortableKeys
  } = usePortableKeys({
    variables: {
      assetIds: allowedAssets.map(({
        assetId
      }) => assetId),
      portableType
    },
    onError
  });
  const {
    completedTask,
    refetch: fetchStatus
  } = useCopyAssetTaskStatus({
    onCompleted: () => setCurrentStep('CONFIRM_SELECTION')
  });
  const {
    mutate: createSyncTaskMutate
  } = useCreateSyncTask({
    onError
  });
  const goToNextStep = useCallback(async () => {
    if (!selectedTargetHub) {
      return;
    }
    const currentIndex = orderedSteps.indexOf(currentStep);
    if (currentIndex === -1) {
      return;
    }
    const nextIndex = currentIndex + 1;
    const nextStep = orderedSteps[nextIndex];
    if (nextIndex < orderedSteps.length) {
      setCurrentStep(nextStep);
    }
    try {
      switch (nextStep) {
        case 'VALIDATION':
          {
            const portableKeys = await fetchPortableKeys();
            await createSyncTaskMutate({
              variables: {
                command: 'DIFF',
                fromHubId: portal.portal_id,
                portableKeys,
                portableType,
                toHubId: selectedTargetHub.hubId
              },
              onCompleted: ({
                syncTask
              }) => {
                void fetchStatus({
                  taskId: syncTask.id
                });
              }
            });
            return;
          }
        case 'AWAITING_ACTION':
          {
            await createSyncTaskMutate({
              variables: {
                command: 'SYNC',
                fromHubId: portal.portal_id,
                portableKeys: allPortableKeys,
                portableType,
                toHubId: selectedTargetHub.hubId
              },
              onCompleted: () => {
                setCurrentStep('SUCCESS');
                onCopyComplete === null || onCopyComplete === void 0 || onCopyComplete();
              }
            });
            return;
          }
        default:
          return;
      }
    } catch (_unused) {
      onError();
    }
  }, [allPortableKeys, createSyncTaskMutate, currentStep, fetchPortableKeys, fetchStatus, onCopyComplete, portableType, portal.portal_id, selectedTargetHub]);
  const [copyableAssets, skippedAssets] = useMemo(() => {
    const _copyableAssets = [];
    const _skippedAssets = [...disallowedAssets];
    if (!completedTask) {
      return [_copyableAssets, _skippedAssets];
    }
    allowedAssets.forEach(asset => {
      var _completedTask$diffSu;
      const portableKey = portableKeyMap[asset.assetId];
      const canCopy = ((_completedTask$diffSu = completedTask.diffSummary) === null || _completedTask$diffSu === void 0 || (_completedTask$diffSu = _completedTask$diffSu.entriesOnlyOnLeft) === null || _completedTask$diffSu === void 0 ? void 0 : _completedTask$diffSu.find(entry => entry.portableKey === portableKey)) !== undefined;
      if (canCopy) {
        _copyableAssets.push(asset);
      } else {
        _skippedAssets.push(Object.assign({}, asset, {
          skipReason: 'ALREADY_COPIED'
        }));
      }
    });
    return [_copyableAssets, _skippedAssets];
  }, [allowedAssets, disallowedAssets, completedTask, portableKeyMap]);
  return /*#__PURE__*/_jsx(AssetCopierContext.Provider, {
    value: {
      copyableAssets,
      skippedAssets,
      currentStep,
      setCurrentStep,
      goToNextStep: () => void goToNextStep(),
      selectedTargetHub,
      setSelectedTargetHub
    },
    children: children
  });
}
export const useAssetCopierContext = () => {
  const context = useContext(AssetCopierContext);
  if (!context) {
    throw new Error('useAssetCopier must be used within an AssetCopierProvider');
  }
  return context;
};